import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { Form } from 'antd';
import styled from 'styled-components';

import { getUsers } from 'apis/UserAPI';
import Input from 'components/lib/Input';
import Select from 'components/lib/Select';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import { useDebounce } from 'utils/hooks/useDebounce';

import { useCompaniesCreateEditModalContext } from './CompaniesCreateEditModalContext';
import { COMPANY_KEYS, PRIMARY_USER_FIELDS } from './constants';
import { EMAIL_REGEX } from '../../../constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 14px;
  padding-bottom: 30px;

  .ant-form-item {
    margin-bottom: 10px;
  }

  .ant-form-item
    .ant-form-item-label
    > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    display: none;
  }
  .ant-form-item .ant-form-item-label > label::after {
    content: '';
    margin-inline: 0px;
  }
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const Label = styled.div`
  font-size: 14px;
  margin-right: 10px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  min-width: 140px;
  display: flex;
  justify-content: flex-end;
`;

const StyledInput = styled(Input)`
  width: 342px;
  height: 36px;
`;

const StyledSelect = styled(Select)`
  min-width: 342px;
  height: 36px;
`;

type PrimaryUserOptionType = {
  label: string;
  value: number;
};

const CompaniesCreateEditModalContentPrimary = () => {
  const [form] = Form.useForm();
  const { company, handleChange } = useCompaniesCreateEditModalContext();

  const isEditMode = Boolean(company?.id);

  const [userSearch, setUserSearch] = useState('');
  const userDebouncedSearch = useDebounce(userSearch, 300);

  const primaryUserId = Form.useWatch(COMPANY_KEYS.PRIMARY_USER_ID, form);

  const { data: userSearchOptions } = useQuery<any, any, PrimaryUserOptionType[]>({
    queryKey: ['company-primary-user', userDebouncedSearch, company?.id],
    queryFn: () =>
      getUsers({
        search: userDebouncedSearch,
        companies: isEditMode && company?.id ? [company.id] : undefined,
        page_size: PAGINATION_PAGE_SIZE.MODERATE,
      }),
    select: (data: any) =>
      (data?.results ?? []).map(
        (user: any): PrimaryUserOptionType => ({
          label: user?.full_name?.length ? user.full_name : user.email || user.username,
          value: user.id,
        })
      ),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: isEditMode,
  });

  const selectedPrimaryUserOption = useMemo<PrimaryUserOptionType | null>(() => {
    const user = company?.primary_user;
    if (!user?.id) {
      return null;
    }

    const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();

    return {
      label: fullName || user?.email || 'Primary user',
      value: user.id,
    };
  }, [company?.primary_user]);

  const primaryUserOptions = useMemo<PrimaryUserOptionType[]>(() => {
    const apiUserOptions = userSearchOptions || [];
    const base = selectedPrimaryUserOption ? [selectedPrimaryUserOption, ...apiUserOptions] : apiUserOptions;
    return [...new Map(base.map((item) => [item.value, item])).values()];
  }, [selectedPrimaryUserOption, userSearchOptions]);

  useEffect(() => {
    form.setFieldsValue(company);
  }, [company, form]);

  const onValuesChange = (changedValues: any) => {
    const [key, value] = Object.entries(changedValues)[0];
    handleChange(key as keyof typeof company, value);
  };

  return (
    <Container>
      <Title>Primary User</Title>
      <Form form={form} layout="horizontal" onValuesChange={onValuesChange}>
        {!isEditMode &&
          PRIMARY_USER_FIELDS.map((field) => (
            <Form.Item
              key={field.key}
              label={<Label>{field.label}</Label>}
              name={field.key}
              rules={[
                {
                  required: field.key !== COMPANY_KEYS.P_C_PHONE,
                  message: `${field.label} is required`,
                },
                field.key === COMPANY_KEYS.P_C_EMAIL ? { pattern: EMAIL_REGEX, message: 'Invalid email format' } : {},
              ]}
            >
              <StyledInput placeholder={field.placeholder} />
            </Form.Item>
          ))}

        {isEditMode && (
          <Form.Item
            label={<Label>Primary user</Label>}
            name={COMPANY_KEYS.PRIMARY_USER_ID}
            rules={[
              {
                required: true,
                message: 'Primary user is required',
              },
            ]}
          >
            <StyledSelect
              className="rectangular"
              showSearch
              allowClear
              filterOption={false}
              placeholder="Search users in this company"
              onSearch={(value) => setUserSearch(value)}
              options={primaryUserOptions}
              value={primaryUserId ?? undefined}
            />
          </Form.Item>
        )}
      </Form>
    </Container>
  );
};

export default CompaniesCreateEditModalContentPrimary;
